import React, { FC, useMemo } from 'react';
import { Box, keyframes } from '@mui/material';
import { Snowflake } from './Snowflake';
import { ChristmasColors } from './christmasColors';

const FLAKE_COLORS = [
  ChristmasColors.SNOW_EDGE,
  ChristmasColors.SNOW_BLUE,
  ChristmasColors.SNOW_LIGHT,
  ChristmasColors.SNOW_EDGE,
  ChristmasColors.SNOW_BLUE,
];

const fall = keyframes`
  0%   { transform: translate3d(0, -10%, 0) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  50%  { transform: translate3d(var(--sway), 50vh, 0) rotate(90deg); }
  90%  { opacity: 1; }
  100% { transform: translate3d(calc(var(--sway) * -1), 105vh, 0) rotate(180deg); opacity: 0; }
`;

export interface FallingSnowProps {
  /** Number of snowflakes. */
  count?: number;
  /** Seconds a flake takes to fall. Each flake gets a random duration between min and max. */
  minDuration?: number;
  maxDuration?: number;
  /** Max flake size in px. */
  maxSize?: number;
  /** Covers the viewport (`fixed`) or just the parent (`absolute`). */
  position?: 'fixed' | 'absolute';
  zIndex?: number;
  /** Loop forever (loader) or fall once (easter egg). */
  loop?: boolean;
}

/** Decorative, non-interactive falling snow. Respects `prefers-reduced-motion`. */
export const FallingSnow: FC<FallingSnowProps> = ({
  count = 40,
  minDuration = 7,
  maxDuration = 13,
  maxSize = 22,
  position = 'fixed',
  zIndex = 1500,
  loop = false,
}) => {
  const flakes = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * (loop ? maxDuration : 3),
        duration: minDuration + Math.random() * (maxDuration - minDuration),
        size: Math.round(maxSize * (0.4 + Math.random() * 0.6)),
        sway: Math.round((Math.random() - 0.5) * 120),
        color: FLAKE_COLORS[i % FLAKE_COLORS.length],
      })),
    [count, loop, maxDuration, minDuration, maxSize],
  );

  return (
    <Box
      aria-hidden
      data-testid='FallingSnow'
      sx={{
        position,
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex,
        '@media (prefers-reduced-motion: reduce)': { display: 'none' },
      }}>
      {flakes.map((flake) => (
        // The keyframes must be referenced from `sx` (Emotion) to be injected; inline `style` can't resolve them.
        // Per-flake values go in `style` so all flakes share one generated class.
        <Box
          key={flake.id}
          data-testid='FallingSnowflake'
          sx={{
            position: 'absolute',
            top: 0,
            lineHeight: 0,
            animation: `${fall} linear ${loop ? 'infinite' : '1'} both`,
            willChange: 'transform, opacity',
            filter: `drop-shadow(0 0 1px ${ChristmasColors.SNOW})`,
          }}
          style={
            {
              left: `${flake.left}%`,
              '--sway': `${flake.sway}px`,
              animationDuration: `${flake.duration}s`,
              animationDelay: `${flake.delay}s`,
            } as React.CSSProperties
          }>
          <Snowflake size={flake.size} color={flake.color} />
        </Box>
      ))}
    </Box>
  );
};

export default FallingSnow;
